import { ACTION_TYPE, JobBuilderContext } from "context/jobBuilderContext";
import { Grid } from "@mui/material";
import { ReactNode, useContext, useEffect, useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "context/appContext";
import VehicleLookup from "components/widgets/VehicleLookup";
import AuturaTextField from "components/inputs/AuturaTextField";
import ReactGA from "react-ga4";
import AuturaStateField from "components/inputs/AuturaStateField";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import AuturaMakeModelField from "components/inputs/AuturaMakeModelField";
import TraxeroDateField from "components/inputs/DatePicker";

export enum SearchType {
  VIN = "VIN",
  PLATE = "Plate",
  MAKE_MODEL = "Make/Model",
}

const VehicleStep = (): ReactNode => {
  const { t } = useTranslation();
  const { state: appState } = useContext(AppContext);
  const { state: jobState, dispatch } = useContext(JobBuilderContext);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

    const handleSetPlateExp = (date: Date | null) => {
      dispatch({
        type: ACTION_TYPE.SET_PLATE_EXP,
        payload: date,
      });
    };

  return (
    <Grid container width={"100%"} spacing={1.5}>
      <Grid mb={3} size={12}>
        <VehicleLookup
          searching={searching}
          setSearching={setSearching}
          autofillVehicle={(vehicle) => {
            ReactGA.event({
              category: "job",
              action: "vehicle_lookup",
              label: "vehicle_lookup_success",
              value: 1,
            });
            dispatch({
              type: ACTION_TYPE.SET_VEHICLE,
              payload: vehicle,
            });
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.plate.number", { ns: "entities" })}
          name="vehicle.plateNumber"
          value={jobState.plateNumber || ""}
          onChange={(e) => {
            const value = e.target.value || "";
            const formatted = value.replace(/[^a-zA-Z0-9]/g, "");
            dispatch({
              type: ACTION_TYPE.SET_PLATE_NUMBER,
              payload: formatted.toUpperCase(),
            });
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaStateField
          label={t("job.plate.state", { ns: "entities" })}
          stateCode={jobState.plateState || ""}
          setStateCode={(stateCode) => {
            dispatch({
              type: ACTION_TYPE.SET_PLATE_STATE,
              payload: stateCode,
            });
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <TraxeroDateField
          label={t("vehicle.plate.exp", { ns: "entities" })}
          date={jobState.plateExp}
          views={["month","year"]}
          setDate={handleSetPlateExp}
          disabled={searching}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.vehicle.vin", { ns: "entities" })}
          name="vehicle.vin"
          value={jobState.vin || ""}
          onChange={(e) => {
            const value = e.target.value || "";
            const formatted = value.replace(/[^a-zA-Z0-9]/g, "");
            dispatch({
              type: ACTION_TYPE.SET_VIN,
              payload: formatted.toUpperCase(),
            });
          }}
          disabled={searching}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.vehicle.year", { ns: "entities" })}
          name="vehicle.year"
          value={jobState.year || ""}
          onChange={(e) => {
            dispatch({
              type: ACTION_TYPE.SET_YEAR,
              payload: e.target.value,
            });
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaDropdown
          label={t("job.vehicle.color", { ns: "entities" })}
          name="vehicle.color"
          value={jobState.color || "other"}
          setValue={(e: ChangeEvent<HTMLInputElement>) => {
            dispatch({
              type: ACTION_TYPE.SET_COLOR,
              payload: e.target.value,
            });
          }}
          options={appState.colors}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <AuturaMakeModelField
          make={jobState.make}
          model={jobState.model}
          disabled={searching}
          setMake={(make) => {
            dispatch({
              type: ACTION_TYPE.SET_MAKE,
              payload: make,
            });
          }}
          setModel={(model) => {
            dispatch({
              type: ACTION_TYPE.SET_MODEL,
              payload: model,
            });
          }}
        />
      </Grid>
    </Grid>
  );
};

export default VehicleStep;
